import {
  Box,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography
} from "@mui/material"
import { hasRole } from "auth/hasRole"
import { useApiDelete, useApiGet, useApiPost, useApiPut } from "auth/useApi2"
import { AppContext } from "components/AppContextProvider"
import Button from "components/Button"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import { useQuery } from "components/useQuery"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { allRoutes } from "generated/routes"
import WorkshopSidebar from "shared/customer/Workshop/WorkshopSidebar"
import React, { useContext, useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { CompanyForm } from "../../components/CompanyForm"
import SubTabs from "components/SubTabs"
import { PageTabs } from "components/navigation/PageTabs/PageTabs"
import { useTranslation } from "react-i18next"
import ConfirmDepartmentRemoveModal from "shared/customer/ConfirmDepartmentRemoveModal"


const EditCompany = ({ match }) => {
  const urlQuery = useQuery()
  const [customerResult, getCustomer] = useApiGet()
  const [companyResult, getCompany] = useApiGet()
  const [updateCompanyResult, updateCompany] = useApiPut()
  const [deleteResult, deleteCompany, resetDeleteState] = useApiDelete()
  const [checkConflictsResult, checkConflicts, resetCheckConflicts] = useApiPost()
  const [pendingSaveData, setPendingSaveData] = useState(null)
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
  const [clickedWorkshop, setClickedWorkshop] = useState(undefined)
  const appContext = useContext(AppContext)
  const { t } = useTranslation()

  const routerHistory = useHistory()
  const [tabValue, setTabValue] = useState(0)
  const tabs = [
    { index: 0, label: t("global.company_information", "Company information"), queryParam: "info" },
    { index: 1, label: t("global.departsments", "Departments"), queryParam: "departments" },
    { index: 2, label: t("global.crisis_information", "Crisis information"), queryParam: "crisisinfo" }
  ]

  if (hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin)) {
    tabs.push({
      index: 3,
      label: t("global.workshops", "Workshops"),
      queryParam: "workshops"
    })
  }

  const [showSidebar, setShowSidebar] = React.useState(false)

  useEffect(() => {
    if (deleteResult.status === 2)
      routerHistory.push(allRoutes.SystemadminCustomersEditCompanies(match.params.id))
  }, [deleteResult.status, routerHistory, match])

  const loadingContext = useContext(GlobalLoaderContext)

  const loadStuff = async () => {
    await getCustomer(apiEndpoints.systemadmincustomers.getcustomerbyid.replace("{id}", match.params.id))
    await getCompany(apiEndpoints.systemadmincustomers.getcompanybyid.replace("{id}", match.params.companyId))
  }

  useEffect(() => {
    if (match.params.id) {
      loadStuff()
    }
  }, [match.params.id])

  useEffect(() => {
    loadingContext.setLoading(() => customerResult.status === 1)
  })

  useEffect(() => {
    loadingContext.setLoading(() => companyResult.status === 1)
  })

  const handleSubmit = (saveCompany) => {
    console.log("saveCompany", saveCompany)
    setPendingSaveData(saveCompany)
    checkConflicts(apiEndpoints.systemadmincustomers.checkdepartmentconflicts, saveCompany)
  }

  useEffect(() => {
    if (checkConflictsResult.status !== 2 || !pendingSaveData) return
    const conflicts = checkConflictsResult.data ?? []
    if (conflicts.length > 0) {
      setConflictDialogOpen(true)
    } else {
      updateCompany(apiEndpoints.systemadmincustomers.updatecompany, pendingSaveData)
      resetCheckConflicts()
      setPendingSaveData(null)
    }
  }, [checkConflictsResult.status])

  const closeConflictDialog = () => {
    setConflictDialogOpen(false)
    resetCheckConflicts()
    setPendingSaveData(null)
  }

  const confirmAndSave = () => {
    updateCompany(apiEndpoints.systemadmincustomers.updatecompany, { ...pendingSaveData, confirmDepartmentDelete: true })
    setConflictDialogOpen(false)
    resetCheckConflicts()
    setPendingSaveData(null)
  }

  const [anchorEl, setAnchorEl] = React.useState(null)

  const open = Boolean(anchorEl)

  const openSettingsDropdown = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const closeSettingsDropdown = () => {
    setAnchorEl(null)
  }

  const [dialogOpen, setDialogOpen] = React.useState(false)

  const openRemoveDialog = () => {
    resetDeleteState()
    setDialogOpen(true)
  }

  const closeRemoveDialog = () => {
    resetDeleteState()
    setDialogOpen(false)
  }

  useEffect(() => {
    const tabParam = urlQuery.get("tab")
    if (!tabParam) return
    tabs.find((tab) => {
      if (tab.queryParam === tabParam.toLowerCase()) {
        setTabValue(tab.index)
        return true
      }
    })
  }, [urlQuery])

  return (
    <div>
      <PageTabs
        routeBack
        tabs={[
          {
            path: allRoutes.SystemadminCustomersEditCompanies(match.params.id),
            title: t("global.back", "Back")
          }
        ]}
      />
      <Container>
        <Box mb={5}>
          <Grid item container direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ height: "58px" }} variant="h2">
              {t("global.edit_company", "Edit company")}
            </Typography>
            {companyResult.status === 2 && tabValue === 3 ? (
              <WorkshopSidebar
                handleSubmit={loadStuff}
                workshop={clickedWorkshop}
                companyId={companyResult.data.id}
              />
            ) : (
              <></>
            )}
          </Grid>
        </Box>
        {companyResult.status === 2 && customerResult.status === 2 && (
          <>
            <SubTabs tabs={tabs} value={tabValue} setValue={setTabValue} marginLeft={1} />
            <CompanyForm
              customer={customerResult.data}
              company={companyResult.data}
              openRemoveDialog={openRemoveDialog}
              onSubmit={handleSubmit}
              result={
                checkConflictsResult.status === 1 || checkConflictsResult.status === 3
                  ? checkConflictsResult
                  : conflictDialogOpen
                  ? { status: 0, messages: [], data: null }
                  : updateCompanyResult
              }
              tabValue={tabValue}
              setCurrentWorkshop={setClickedWorkshop}
            />
          </>
        )}
        <ConfirmDepartmentRemoveModal
          conflictDialogOpen={conflictDialogOpen}
          closeConflictDialog={closeConflictDialog}
          confirmAndSave={confirmAndSave}
          checkConflictsResult={checkConflictsResult}
        />
        <Grid container spacing={2}>
          <Dialog
            open={dialogOpen}
            onClose={closeSettingsDropdown}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">{`${t(
              "settings.form.button.remove_company",
              "Remove company"
            )}?`}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {t(
                  "settings.companies.are_you_sure_delete_company",
                  "Are you sure you want to remove the company? Your choice cannot be undone."
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeRemoveDialog} variant="outlined">
                {t("global.cancel", "Cancel")}
              </Button>
              <Button
                result={deleteResult}
                onClick={() => {
                  deleteCompany(
                    apiEndpoints.systemadmincustomers.deletecompany.replace("{id}", match.params.companyId)
                  )
                }}
                color="primary"
                autoFocus>
                {t("global.yes_im_sure", "Yes, I'm sure")}
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Container>
    </div>
  )
}

export default EditCompany
